import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, User, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';
import { DynamicLogo } from './DynamicLogo';
import { isAuthenticated } from '@/services/memberApi';
import { Cart } from './Cart';

const NAV_LINKS: { path: string; label: string; external?: boolean }[] = [
  { path: '/',              label: 'Home' },
  { path: '/shop',           label: 'Shop' },
  { path: '/skin-analysis', label: 'Analysis' },
  { path: '/about',         label: 'About' },
  { path: '/blog',          label: 'Journal' },
  { path: '/agent-portal',  label: 'Agent' },
  { path: '/member/dashboard', label: 'Member Portal' },
];

export function Header() {
  const [isMenuOpen, setIsMenuOpen]     = useState(false);
  const [isScrolled, setIsScrolled]     = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery]   = useState('');
  const [authed, setAuthed]             = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Auth state — check on mount and on storage changes
  useEffect(() => {
    setAuthed(isAuthenticated());
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'marple_member_token') setAuthed(!!e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Cross-tab auth sync
  useEffect(() => {
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel('marple_auth');
      channel.onmessage = (e) => {
        if (e.data?.type === 'LOGIN')  setAuthed(true);
        if (e.data?.type === 'LOGOUT') setAuthed(false);
      };
    } catch { /* not supported */ }
    return () => channel?.close();
  }, []);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (isSearchOpen) setTimeout(() => searchInputRef.current?.focus(), 50);
  }, [isSearchOpen]);

  // Close menu on route change
  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setIsSearchOpen(false);
      setSearchQuery('');
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <>
      {/* Free Delivery Banner */}
      <div className="fixed top-0 left-0 right-0 z-50 text-white text-center py-2 text-sm font-medium" style={{ background: '#567D38' }}>
        🌿 Free delivery on orders over R650 · Dermatologically Tested
      </div>

      <header
        className={cn(
          'fixed top-10 left-0 right-0 z-40 transition-all duration-300',
          isScrolled ? 'bg-white shadow-md py-3' : 'bg-white/95 py-4'
        )}
      >
        <div className="container-marple">
          <div className="flex items-center justify-between">

            {/* Logo */}
            <Link to="/" className="relative z-10 flex-shrink-0">
              <DynamicLogo variant="default" size="md" showText />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map((link) =>
                link.external ? (
                  <a
                    key={link.path}
                    href={link.path}
                    className="relative px-4 py-2 font-display text-sm font-semibold uppercase tracking-wider
                               text-[#6B7B6E] hover:text-[#567D38] hover:bg-[#F7F5EE] rounded-md transition-all duration-200"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={cn(
                      'relative px-4 py-2 font-display text-sm font-semibold uppercase tracking-wider rounded-md transition-all duration-200',
                      isActive(link.path)
                        ? 'text-[#567D38] bg-[#BDD630]/10'
                        : 'text-[#6B7B6E] hover:text-[#567D38] hover:bg-[#F7F5EE]'
                    )}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2">

              {/* Search */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className="hidden md:flex p-2 text-[#6B7B6E] hover:text-[#567D38] hover:bg-[#F7F5EE] rounded-full transition-colors"
                aria-label="Search"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Auth-aware account area */}
              <div className="hidden md:flex items-center gap-1">
                {authed ? (
                  <Link
                    to="/member/dashboard"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#567D38] bg-[#BDD630]/15 hover:bg-[#BDD630]/25 rounded-full transition-colors"
                  >
                    <User className="w-4 h-4" />
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/member/create-account"
                      className="px-3 py-1.5 text-sm font-medium text-[#6B7B6E] hover:text-[#567D38] hover:bg-[#F7F5EE] rounded-full transition-colors"
                    >
                      Sign Up
                    </Link>
                    <Link
                      to="/member/login"
                      className="p-2 text-[#6B7B6E] hover:text-[#567D38] hover:bg-[#F7F5EE] rounded-full transition-colors"
                      aria-label="Sign in"
                    >
                      <User className="w-5 h-5" />
                    </Link>
                  </>
                )}
              </div>

              {/* Cart */}
              <div className="hidden md:flex">
                <Cart />
              </div>

              {/* Quiz CTA */}
              <Link
                to="/skin-analysis"
                className="hidden md:flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-full transition-all duration-300 hover:scale-105"
                style={{ background: '#BDD630', color: '#567D38' }}
              >
                Read My Skin
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(true)}
                className="lg:hidden p-2 text-[#567D38] hover:bg-[#F7F5EE] rounded-lg transition-colors"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Mobile slide-from-left drawer ──────────────────────────────── */}
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/50 transition-opacity duration-300 lg:hidden',
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none invisible'
        )}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden="true"
      />
      {/* Drawer */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-2xl transition-transform duration-300 lg:hidden flex flex-col',
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 py-5 border-b border-[#F0EBE3]">
          <DynamicLogo variant="default" size="sm" showText />
          <button
            onClick={() => setIsMenuOpen(false)}
            className="p-2 rounded-lg text-[#6B7B6E] hover:bg-[#F7F5EE] transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {NAV_LINKS.map((link) =>
            link.external ? (
              <a
                key={link.path}
                href={link.path}
                className="flex items-center justify-between px-4 py-3 rounded-xl font-display text-sm font-semibold uppercase tracking-wide text-[#6B7B6E] hover:text-[#567D38] hover:bg-[#F7F5EE] transition-colors"
              >
                {link.label}
                <ChevronRight className="w-4 h-4 opacity-40" />
              </a>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                className={cn(
                  'flex items-center justify-between px-4 py-3 rounded-xl font-display text-sm font-semibold uppercase tracking-wide transition-colors',
                  isActive(link.path)
                    ? 'text-[#567D38] bg-[#BDD630]/10'
                    : 'text-[#6B7B6E] hover:text-[#567D38] hover:bg-[#F7F5EE]'
                )}
              >
                {link.label}
                <ChevronRight className="w-4 h-4 opacity-40" />
              </Link>
            )
          )}
        </nav>

        {/* Drawer footer CTAs */}
        <div className="px-5 py-5 border-t border-[#F0EBE3] space-y-3">
          <Link
            to="/skin-analysis"
            className="flex items-center justify-center w-full py-3 rounded-full font-semibold text-sm"
            style={{ background: '#BDD630', color: '#567D38' }}
          >
            Read My Skin
          </Link>
          {authed ? (
            <Link
              to="/member/dashboard"
              className="flex items-center justify-center w-full py-3 rounded-full font-semibold text-sm border-2"
              style={{ borderColor: '#567D38', color: '#567D38' }}
            >
              My Dashboard
            </Link>
          ) : (
            <Link
              to="/member/create-account"
              className="flex items-center justify-center w-full py-3 rounded-full font-semibold text-sm border-2"
              style={{ borderColor: '#567D38', color: '#567D38' }}
            >
              Create Free Account
            </Link>
          )}
        </div>
      </div>

      {/* ── Search overlay ──────────────────────────────────────────────── */}
      <div
        className={cn(
          'fixed inset-0 z-50 flex items-start justify-center pt-24 px-4 transition-opacity duration-300',
          isSearchOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none invisible'
        )}
        style={{
          background: 'rgba(26,43,31,0.85)',
          backdropFilter: isSearchOpen ? 'blur(6px)' : 'none',
          WebkitBackdropFilter: isSearchOpen ? 'blur(6px)' : 'none',
        }}
        onClick={(e) => { if (e.target === e.currentTarget) setIsSearchOpen(false); }}
      >
        <div className="w-full max-w-xl">
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7B6E]" />
            <input
              ref={searchInputRef}
              type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search products…"
              className="w-full pl-14 pr-14 py-5 text-lg rounded-2xl bg-white text-[#1A2E12] placeholder:text-[#6B7B6E] focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setIsSearchOpen(false)}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full text-[#6B7B6E] hover:bg-[#F7F5EE] transition-colors"
              aria-label="Close search"
            >
              <X className="w-5 h-5" />
            </button>
          </form>
          <p className="text-white/50 text-sm text-center mt-4">Press Enter to search · Esc to close</p>
        </div>
      </div>

      {/* Spacer */}
      <div className="h-[120px]" />
    </>
  );
}

export default Header;
